import { useState } from 'react';
import { Calendar, Plus, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime, formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';

interface Appointment {
  id: string;
  leadId: string;
  leadName: string;
  leadPhone: string;
  type: 'opd' | 'ipd' | 'followup' | 'surgery';
  title: string;
  scheduledAt: string;
  doctorName?: string;
  location?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  notes?: string;
  createdAt: string;
}

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'opd', label: 'OPD' },
  { value: 'ipd', label: 'IPD' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'surgery', label: 'Surgery' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-50 text-blue-700',
  completed: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-600',
  rescheduled: 'bg-amber-50 text-amber-700',
};

const TYPE_COLORS: Record<string, string> = {
  opd: 'bg-cyan-50 text-cyan-700',
  ipd: 'bg-purple-50 text-purple-700',
  followup: 'bg-yellow-50 text-yellow-700',
  surgery: 'bg-red-50 text-red-700',
};

export function AppointmentsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ type: '', status: '', dateFrom: '', dateTo: '' });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['appointments', page, filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        data: { data: Appointment[]; pagination: { total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } };
      }>('/appointments', { params: { page, limit: 20, ...filters } });
      return data.data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await apiClient.patch(`/appointments/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Appointment updated');
    },
    onError: () => toast.error('Failed to update appointment'),
  });

  const todayCount = data?.data.filter(a => {
    const d = new Date(a.scheduledAt);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar size={24} className="text-brand-navy" />
            Appointments
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{todayCount} appointments today</p>
        </div>
        <Button variant="outline" size="sm" icon={<RefreshCw size={14} />} onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Scheduled', value: data?.data.filter(a => a.status === 'scheduled').length ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Completed', value: data?.data.filter(a => a.status === 'completed').length ?? 0, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Cancelled', value: data?.data.filter(a => a.status === 'cancelled').length ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Rescheduled', value: data?.data.filter(a => a.status === 'rescheduled').length ?? 0, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-sm text-slate-600 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <Select label="Type" options={TYPE_OPTIONS} value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} />
          <Select label="Status" options={STATUS_OPTIONS} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} />
          <Input label="From Date" type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
          <Input label="To Date" type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
        </div>
      </div>

      {/* Appointments List */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : (
          <>
            <div className="divide-y divide-slate-50">
              {data?.data.map(apt => (
                <div key={apt.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-brand-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar size={18} className="text-brand-navy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{apt.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[apt.type] ?? 'bg-slate-100 text-slate-600'}`}>
                            {apt.type.toUpperCase()}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[apt.status] ?? 'bg-slate-100 text-slate-600'}`}>
                            {apt.status}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-4 flex-wrap text-sm text-slate-500">
                          <span className="font-medium text-slate-700">{apt.leadName}</span>
                          <span>{apt.leadPhone}</span>
                          {apt.doctorName && <span>Dr. {apt.doctorName}</span>}
                          {apt.location && <span>{apt.location}</span>}
                        </div>
                        {apt.notes && (
                          <p className="mt-1 text-xs text-slate-500 truncate">{apt.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock size={12} />
                        {formatDateTime(apt.scheduledAt)}
                      </div>
                      {apt.status === 'scheduled' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => updateStatus.mutate({ id: apt.id, status: 'completed' })}
                            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Mark completed"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => updateStatus.mutate({ id: apt.id, status: 'cancelled' })}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Cancel"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {!data?.data.length && (
                <div className="py-16 text-center text-slate-400">
                  <Calendar size={40} className="mx-auto mb-3 opacity-30" />
                  <p>No appointments found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {data && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Total: {data.pagination.total}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="text-xs text-slate-500 flex items-center px-3">{page} / {data.pagination.totalPages}</span>
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
